/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { fg } from '@confluence/feature-gating';
import { toDocumentNode } from '@confluence/apollo-relay-interop/entry-points/toDocumentNode';

import { colorCodeToColorName, tokenToHex } from '../helpers';

import type {
	ContentStateGroupSetContentStateMutation as ContentStateGroupSetContentStateMutation$data,
	ContentStateGroupSetContentStateMutationVariables as ContentStateGroupSetContentStateMutation$variables,
} from './__types__/ContentStateGroupSetContentStateMutation';
import EditorContentStateQuery from './__generated__/EditorContentStateQuery.graphql';
import { ContentState } from './ContentState';
import { i18n } from './i18n';
import type { ContentStateGroupProps, ContentState as ContentStateType } from './types';
import { customGroupLabel } from './types';
import { createNewCustomStatesArrayWhenExistingCustomSelected } from './helpers';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupLabel = styled.div({
	marginBottom: token('space.150'),
	font: token('font.body'),
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.medium'),
	marginLeft: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentStateListWrapper = styled.ul({
	listStyleType: 'none',
	margin: token('space.0'),
	padding: token('space.0'),
});

export const ContentStateGroupComponent: FC<ContentStateGroupProps> = ({
	contentId,
	contentStateState,
	groupIsEnabled,
	groupLabel,
	contentStates,
	flags,
	userOffline,
	intl: { formatMessage },
	onUpdateSuccess,
	setIsContentStateMenuOpen,
}) => {
	// Need these refs to store original content states/array
	// in the case of an error (ex: user picks a state but somehow admin turned off at the same time) we need to
	// show the original values since we set it right after we mutate and there's no way to check if there are any
	// errors until after we mutate
	const originalContentState = useRef(contentStateState.state.contentState);
	const originalCustomStatesArray = useRef(contentStateState.state.customStatesArray);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [shouldSetAutomationFlag, setShouldSetAutomationFlag] = useState(false);
	useEffect(() => {
		if (
			!localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE) &&
			fg('cc_a4c_smart_button_changeboarding')
		) {
			setShouldSetAutomationFlag(true);
		}
	}, []);
	const isLivePage = useIsCurrentPageLive();
	const [setContentStateMutation] = useMutation<
		ContentStateGroupSetContentStateMutation$data,
		ContentStateGroupSetContentStateMutation$variables
	>(
		gql`
			mutation ContentStateGroupSetContentStateMutation(
				$contentId: ID!
				$contentStateInput: ContentStateInput!
			) {
				setContentState(contentId: $contentId, contentState: $contentStateInput) {
					name
					color
					id
					restrictionLevel
				}
			}
		`,
		{
			onError: () => {
				// rollback to originals since they got updated and show error flag
				void contentStateState.setContentState(originalContentState.current);
				void contentStateState.setCustomStatesArray(originalCustomStatesArray.current);
				void flags.showErrorFlag({
					id: 'content-state-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						objectId: contentId,
						name: 'contentStateErrorFlag',
						attributes: {
							isLivePage,
						},
					},
				}).fire();
			},
			onCompleted: () => {
				// If a mutation was successfully completed, save the state/array in case an error occurs and we need to
				// rollback to these values
				originalContentState.current = contentStateState.state.contentState;
				originalCustomStatesArray.current = contentStateState.state.customStatesArray;
				void contentStateState.setIsUpdating(false);
				onUpdateSuccess?.();
			},
			// Need to refetch since our cache isn't updating correctly - temporary solution until BE fixes
			refetchQueries: () => [
				{
					query: toDocumentNode(EditorContentStateQuery),
					variables: {
						contentId,
						isDraft: !isLivePage,
					},
				},
			],
		},
	);

	const handleClick = useCallback(
		(newContentState: ContentStateType, label: string, index: number) => {
			void contentStateState.setIsUpdating(true);
			void setContentStateMutation({
				variables: {
					contentId,
					contentStateInput: {
						color: tokenToHex(newContentState.color),
						name: newContentState.name,
						id: newContentState.id as any, // TODO fix type
					},
				},
			});
			void contentStateState.setContentState(newContentState);
			setIsContentStateMenuOpen(false);
			// if label selected was a custom one, updated the custom list to add the selected label on top
			if (label === customGroupLabel) {
				const newCustomStatesArray = createNewCustomStatesArrayWhenExistingCustomSelected(
					contentStateState.state.customStatesArray,
					newContentState,
					index,
				);
				void contentStateState.setCustomStatesArray(newCustomStatesArray);
			}
			if (shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				// This flag is used in AutomationMenuButtonWithSmartButtonSpotlight
				// It will check this flag to determine if it should show the spotlight
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: label, // "custom" or "suggested"
						color: colorCodeToColorName(newContentState.color!.toUpperCase()),
						restrictionLevel: newContentState.restrictionLevel?.toUpperCase(),
						uiLocation: 'pageTitleSection',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			setContentStateMutation,
			setIsContentStateMenuOpen,
			isLivePage,
			shouldSetAutomationFlag,
		],
	);
	const contentStateList = contentStates.map((contentState, index) => {
		if (contentState) {
			return (
				<ContentState
					contentState={contentState}
					userOffline={userOffline}
					onClick={() => handleClick(contentState, groupLabel, index)}
					key={`${groupLabel}-content-state-${index}`}
				/>
			);
		}
	});
	return groupIsEnabled && contentStates.length > 0 ? (
		<div data-testid={`${groupLabel.toLowerCase()}-content-states`}>
			<GroupLabel aria-label={groupLabel}>
				{groupLabel === customGroupLabel
					? formatMessage(i18n.custom)
					: formatMessage(i18n.suggested)}
			</GroupLabel>
			<ContentStateListWrapper aria-label={groupLabel}>{contentStateList}</ContentStateListWrapper>
		</div>
	) : null;
};

export const ContentStateGroup = injectIntl(withFlags(ContentStateGroupComponent));
